import { z } from "zod";

/**
 * Finance summary + insights (finances phase) — GET /v2/finance/summary and
 * GET /v2/finance/insights. Every figure here is server-computed (revenue,
 * expenses, net, category rollups, recurring detection, monthly series,
 * top vendors); this file never re-derives a total from raw expense rows,
 * per the house rule that money is server-authoritative and rendered via
 * centsToUsd only.
 */

export const CategoryBreakdownRowSchema = z.object({
  category: z.string(),
  amount_cents: z.number(),
  pct: z.number(),
});
export type CategoryBreakdownRow = z.infer<typeof CategoryBreakdownRowSchema>;

export const RecurringVendorRowSchema = z.object({
  vendor: z.string(),
  monthly_cents: z.number(),
  count: z.number(),
  last_date: z.string(),
});
export type RecurringVendorRow = z.infer<typeof RecurringVendorRowSchema>;

/** "YYYY-MM" -> revenue/expenses/net for the 6-month cash-flow bar chart. */
export const MonthlyFinanceRowSchema = z.object({
  month: z.string(),
  revenue_cents: z.number(),
  expenses_cents: z.number(),
  net_cents: z.number(),
});
export type MonthlyFinanceRow = z.infer<typeof MonthlyFinanceRowSchema>;

export const TopVendorRowSchema = z.object({
  vendor: z.string(),
  amount_cents: z.number(),
});
export type TopVendorRow = z.infer<typeof TopVendorRowSchema>;

export const FinanceSummarySchema = z.object({
  revenue_cents: z.number(),
  expenses_cents: z.number(),
  net_cents: z.number(),
  by_category: z.array(CategoryBreakdownRowSchema),
  recurring: z.array(RecurringVendorRowSchema),
  monthly: z.array(MonthlyFinanceRowSchema),
  top_vendors: z.array(TopVendorRowSchema),
});
export type FinanceSummary = z.infer<typeof FinanceSummarySchema>;

export const InsightsSourceSchema = z.enum(["haiku", "deterministic"]);
export type InsightsSource = z.infer<typeof InsightsSourceSchema>;

export const FinanceInsightsSchema = z.object({
  summary: FinanceSummarySchema,
  narrative: z.string(),
  source: InsightsSourceSchema,
});
export type FinanceInsights = z.infer<typeof FinanceInsightsSchema>;

export interface FinanceDateRangeParams {
  from?: string;
  to?: string;
}
